import React, { useEffect, useRef, useState } from "react";
import {
  Pressable,
  StyleSheet,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import ValidationMessage from "./ValidationMessage";

import { type ValidationError } from "@/schemas/forms/error";
import { isValidUsername } from "@/schemas/forms/username";

type UsernameInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  iconColor?: string;
};

const UsernameInput: React.FC<UsernameInputProps> = ({
  value,
  onChangeText,
  placeholder,
  containerStyle,
  inputStyle,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const inputRef = useRef<TextInput>(null);

  const validateUsername = (username: string): ValidationError | null => {
    const validation = isValidUsername(username);
    if (validation.isValid) {
      return null;
    } else {
      const error = { message: validation.message };
      return error;
    }
  };

  const handleChangeText = (text: string): void => {
    onChangeText(text);
    setValidationError(validateUsername(text));
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    setShowError(true);
  };

  useEffect(() => {
    // const defaultValue =
    //   process.env["NODE_ENV"] === "development" ? "hn531" : "";
    // if (value === "" && defaultValue) {
    //   onChangeText(defaultValue);
    // }
  }, []);

  return (
    <Pressable onPress={() => inputRef.current?.focus()}>
      <View
        testID="username-input"
        style={[
          styles.container,
          containerStyle,
          isFocused && styles.focusedContainer,
          showError && validationError !== null && styles.invalidContainer,
        ]}
      >
        <TextInput
          ref={inputRef}
          style={[styles.input, inputStyle]}
          value={value}
          onChangeText={handleChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          keyboardType={"default"}
          autoCapitalize="none"
          autoCorrect={false}
          secureTextEntry={false}
        />
        {value !== "" && validationError === null && (
          <Ionicons
            name="checkmark-circle"
            size={24}
            color="#48BB78"
            style={styles.validIcon}
          />
        )}
      </View>
      {showError && validationError !== null && (
        <ValidationMessage error={validationError} />
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    backgroundColor: "white",
    paddingHorizontal: 12,
    marginBottom: 4,
  },
  focusedContainer: {
    borderColor: "#4299E1",
  },
  invalidContainer: {
    borderColor: "#F56565",
  },
  input: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 12,
    color: "#2D3748",
  },
  validIcon: {
    marginLeft: 8,
  },
});

export default UsernameInput;
